/**
 * Composizione della seduta (V66), la scheda Componi: quattro gruppi di
 * sistema, custom, blocchi assegnati a uno o più gruppi. Blocchi e Gruppi
 * stanno in due schede del componente Tabs, e si apre sui Blocchi: i blocchi
 * a righe come la libreria, i gruppi in card una sotto l'altra con i loro
 * giocatori, spostati da un menu.
 *
 * All'apertura in scrittura, se mancano, si creano i gruppi di sistema.
 * RPE e minuti si scelgono solo all'inserimento del blocco.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { creaGruppoSchema, dose, type FamigliaAttributo, puo } from '@calcio/shared';
import {
  ActivityIcon,
  ArrowRightLeftIcon,
  ClipboardPenLineIcon,
  ClockIcon,
  InfoIcon,
  LayersIcon,
  OctagonXIcon,
  PencilIcon,
  RulerIcon,
  Trash2Icon,
  UsersIcon,
  UserXIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { ErroreApi, chiama } from '../api/client.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { type RigaRosa, conMedie, etichettaRuolo } from '../giocatori/elenchi.js';
import { RigaGiocatore } from '../giocatori/RigaGiocatore.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { DatiScheda, MixPerFamiglia, RiquadroFamiglia } from '../schede/RigaScheda.js';
import { famigliaPrevalente, type SchedaInElenco } from '../schede/schede.js';
import { CardVista } from '../comuni/Riquadri.js';
import { ComandoIcona, MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { Maglia } from '../comuni/Maglia.js';
import {
  type BloccoSeduta,
  type ComposizioneSeduta as DatiPiano,
  type GiocatoreSeduta,
  type GruppoSeduta,
  gruppoDelGiocatore,
  nomeInRosa,
  risultatiDelBlocco,
} from './allenamenti.js';
import type { TestInElenco } from '../test-misura/test-misura.js';
import { type Domanda, useConferma } from '../comuni/Conferma.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const LIBRERIA = '/schede?tipo=esercizio&attiva=true';

type Vista = 'gruppi' | 'blocchi';

export function ComposizioneSeduta() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const legge = Boolean(ss && puo(utente.assegnazioni, 'allenamento.leggi', ss));
  const { dati, errore, ricarica } = useDati<DatiPiano>(
    legge ? `/allenamenti/${id}/composizione` : '',
  );

  if (!ss || !legge) return <NonTrovata />;
  if (errore) return <StatoCaricamento errore={errore} />;
  if (!dati || dati.allenamento.id !== id) return <StatoCaricamento errore={null} />;
  if (dati.allenamento.squadraStagioneId !== ss) {
    return (
      <Navigate
        to={`/sq/${dati.allenamento.squadraStagioneId}/allenamenti/${id}/blocchi`}
        replace
      />
    );
  }
  return (
    <Piano
      key={id}
      dati={dati}
      ricarica={ricarica}
      scrive={puo(utente.assegnazioni, 'allenamento.gestisci', ss)}
    />
  );
}

function Piano({
  dati,
  ricarica,
  scrive,
}: {
  dati: DatiPiano;
  ricarica: () => Promise<void>;
  scrive: boolean;
}) {
  const a = dati.allenamento;
  const soloLettura = !scrive || a.stato === 'annullato';
  const gruppiChiusi = soloLettura || a.stato === 'svolto';
  const libreria = useDati<SchedaInElenco[]>(LIBRERIA);
  // I test si propongono solo a chi aggiunge blocchi.
  const libreriaTest = useDati<TestInElenco[]>(soloLettura ? '' : '/test?attivo=true');
  const rosaSquadra = useDati<RigaRosa[]>(`/squadre-stagione/${a.squadraStagioneId}/rosa`);
  const [inCorso, setInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();
  const [dialogoGruppo, setDialogoGruppo] = useState<null | 'nuovo' | GruppoSeduta>(null);
  const [libreriaAperta, setLibreriaAperta] = useState(false);
  const defaultChiesto = useRef(false);
  const sistemi = dati.gruppi.filter((g) => g.diSistema).length;
  // Gruppi o blocchi nella query string, sostituiti nella cronologia come le schede.
  const [cerca, setCerca] = useSearchParams();
  // Di default i blocchi; i gruppi con `?vista=gruppi`.
  const vista: Vista = cerca.get('vista') === 'gruppi' ? 'gruppi' : 'blocchi';
  const cambiaVista = (v: Vista) =>
    setCerca(v === 'blocchi' ? {} : { vista: v }, { replace: true });

  useEffect(() => {
    if (!scrive || defaultChiesto.current || sistemi === 4) return;
    if (a.stato === 'annullato') return;
    defaultChiesto.current = true;
    void chiama(`/allenamenti/${a.id}/gruppi/default`, { metodo: 'POST' })
      .then(() => ricarica())
      .catch((e) => {
        defaultChiesto.current = false;
        toast.error(messaggio(e));
      });
  }, [scrive, sistemi, a.id, a.stato, ricarica]);

  const assegna = async (giocatoreId: string, gruppoId: string) => {
    if (gruppoDelGiocatore(dati.gruppi, giocatoreId) === gruppoId) return;
    setInCorso(true);
    try {
      await chiama(`/allenamenti/${a.id}/giocatori/${giocatoreId}/gruppo`, {
        metodo: 'PUT',
        corpo: { gruppoId },
      });
      await ricarica();
    } catch (e) {
      toast.error(messaggio(e));
      await ricarica();
    } finally {
      setInCorso(false);
    }
  };

  const eliminaGruppo = async (gruppo: GruppoSeduta) => {
    const si = await chiedi({
      titolo: `Eliminare il gruppo ${gruppo.nome}?`,
      azione: 'Elimina',
      distruttiva: true,
    });
    if (!si) return;
    setInCorso(true);
    try {
      await chiama(`/allenamenti/${a.id}/gruppi/${gruppo.id}`, { metodo: 'DELETE' });
      await ricarica();
      toast.success(`Gruppo ${gruppo.nome} eliminato.`);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const eliminaBlocco = async (blocco: BloccoSeduta) => {
    const si = await chiedi({
      titolo: `Togliere il blocco ${blocco.nome}?`,
      azione: 'Togli',
      distruttiva: true,
    });
    if (!si) return;
    setInCorso(true);
    try {
      if (!(await togliBlocco(a.id, blocco.id, chiedi))) return;
      await ricarica();
      toast.success(`Blocco ${blocco.nome} tolto.`);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const nomiGruppo = (ids: string[]) =>
    ids.map((id) => dati.gruppi.find((g) => g.id === id)?.nome ?? '').filter(Boolean);

  const senzaGruppo = dati.rosa.filter((g) => gruppoDelGiocatore(dati.gruppi, g.id) === null);
  const puoCreareGruppi = scrive && a.stato === 'programmato';
  const puoAggiungereBlocchi = !soloLettura && dati.gruppi.length > 0;

  const perGiocatore = new Map((rosaSquadra.dati ?? []).map((r) => [r.giocatoreId, conMedie(r)]));
  const conVoti = rosaSquadra.dati?.some((r) => 'votoMedio' in r) ?? false;
  const conMedieAttributi = rosaSquadra.dati?.some((r) => 'medieAttributi' in r) ?? false;

  // Il pulsante a icona apre il menu dei gruppi, con il suo spuntato.
  const sposta = (g: GiocatoreSeduta, gruppoId: string | null) =>
    !gruppiChiusi && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label={`Sposta ${nomeInRosa(g)} in un altro gruppo`}
            title="Sposta in un altro gruppo"
            disabled={inCorso}
          >
            <ArrowRightLeftIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Sposta in</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={gruppoId ?? ''}
            onValueChange={(v) => void assegna(g.id, v)}
          >
            {dati.gruppi.map((gr) => (
              <DropdownMenuRadioItem key={gr.id} value={gr.id}>
                {gr.nome}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );

  // La riga della rosa, con stato, minuti, voto e medie; chi non è più in rosa oggi ha quella essenziale.
  const rigaGiocatore = (g: GiocatoreSeduta, gruppoId: string | null) => {
    const r = perGiocatore.get(g.id);
    return (
      <li key={g.id}>
        {r ? (
          <RigaGiocatore
            riga={r}
            chiusi={false}
            // Il comando della riga è lo spostamento: la scheda giocatore si apre dalla rosa.
            apribile={false}
            conVoti={conVoti}
            conMedie={conMedieAttributi}
            azione={sposta(g, gruppoId)}
          />
        ) : (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
            <Maglia numero={g.numero} ruolo={g.ruoloPreferito} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-semibold">
                {g.cognome} {g.nome}
              </span>
              <span className="text-sm text-muted-foreground">
                {etichettaRuolo(g.ruoloPreferito)}
              </span>
            </span>
            {sposta(g, gruppoId)}
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      {a.stato === 'svolto' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Appello chiuso</AlertTitle>
          <AlertDescription>
            I gruppi non si modificano. Puoi ancora aggiungere o eliminare blocchi; un blocco nuovo
            entra nel Registro seduta.
          </AlertDescription>
        </Alert>
      )}
      {a.stato === 'annullato' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Allenamento annullato</AlertTitle>
          <AlertDescription>
            La composizione si legge, ma non si modifica finché non lo ripristini dal riepilogo.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={vista} onValueChange={(v) => cambiaVista(v as Vista)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="blocchi">
              <LayersIcon />
              Blocchi
              <Badge variant="secondary" className="tabular-nums">
                {dati.blocchi.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="gruppi">
              <UsersIcon />
              Gruppi
              <Badge variant="secondary" className="tabular-nums">
                {dati.gruppi.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
          {vista === 'gruppi' && puoCreareGruppi && (
            <PulsanteNuovo etichetta="Nuovo gruppo" onClick={() => setDialogoGruppo('nuovo')} />
          )}
          {vista === 'blocchi' && puoAggiungereBlocchi && (
            <PulsanteNuovo etichetta="Aggiungi blocco" onClick={() => setLibreriaAperta(true)} />
          )}
        </div>

        <TabsContent value="gruppi" className="flex flex-col gap-4 pt-2">
          {dati.gruppi.length === 0 ? (
            scrive && a.stato !== 'annullato' ? (
              <p className="text-muted-foreground">Creo i gruppi di sistema…</p>
            ) : (
              <StatoVuoto
                titolo="Nessun gruppo"
                descrizione="Mister e Dirigente li creano aprendo la composizione."
              />
            )
          ) : (
            <>
              {!gruppiChiusi && (
                <Nota>
                  Un giocatore sta in un solo gruppo: il pulsante a destra della riga lo sposta.
                </Nota>
              )}
              {/* Una card sotto l'altra; i giocatori, dentro, su due colonne da largo, come la rosa. */}
              <div className="flex flex-col gap-4">
                {senzaGruppo.length > 0 && (
                  <CardVista titolo="Senza gruppo" Icona={UserXIcon} conteggio={senzaGruppo.length}>
                    <ul className="grid gap-2 @5xl:grid-cols-2">
                      {senzaGruppo.map((g) => rigaGiocatore(g, null))}
                    </ul>
                  </CardVista>
                )}
                {dati.gruppi.map((g) => {
                  const t = totaliGruppo(dati.blocchi, g.id);
                  return (
                    <CardVista
                      key={g.id}
                      titolo={g.nome}
                      Icona={UsersIcon}
                      conteggio={g.giocatori.length}
                      nota={
                        t.blocchi === 0
                          ? 'Nessun blocco'
                          : `${t.blocchi} blocc${t.blocchi === 1 ? 'o' : 'hi'} · ${t.minuti}′ · ${t.ua} UA`
                      }
                      azione={
                        !gruppiChiusi &&
                        !g.diSistema && (
                          <MenuAzioni
                            riga
                            etichetta={`Azioni sul gruppo ${g.nome}`}
                            voci={[
                              {
                                etichetta: 'Modifica',
                                Icona: PencilIcon,
                                onSelect: () => setDialogoGruppo(g),
                              },
                              {
                                etichetta: 'Elimina',
                                Icona: Trash2Icon,
                                disabled: inCorso,
                                onSelect: () => void eliminaGruppo(g),
                                distruttiva: true,
                              },
                            ]}
                          />
                        )
                      }
                    >
                      {g.giocatori.length === 0 ? (
                        <StatoVuoto titolo="Nessun giocatore" />
                      ) : (
                        <ul className="grid gap-2 @5xl:grid-cols-2">
                          {g.giocatori.map((j) => rigaGiocatore(j, g.id))}
                        </ul>
                      )}
                    </CardVista>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="blocchi" className="flex flex-col gap-4 pt-2">
          {dati.gruppi.length > 0 && (
            <ul className="grid grid-cols-2 gap-2 @xl:grid-cols-4">
              {dati.gruppi.map((g) => {
                const t = totaliGruppo(dati.blocchi, g.id);
                return (
                  <li
                    key={g.id}
                    className="flex flex-col gap-1 rounded-lg border border-border bg-card p-2.5"
                  >
                    <span className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-semibold">{g.nome}</span>
                      <Badge variant="secondary" className="tabular-nums">
                        {t.blocchi}
                      </Badge>
                    </span>
                    <span className="flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1" title="Minuti programmati">
                        <ClockIcon aria-hidden className="size-4" />
                        <span className="font-medium text-foreground tabular-nums">
                          {t.minuti}′
                        </span>
                      </span>
                      <span className="flex items-center gap-1" title="Dose target">
                        <ActivityIcon aria-hidden className="size-4" />
                        <span className="font-medium text-foreground tabular-nums">{t.ua} UA</span>
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <CardVista titolo="Blocchi" Icona={LayersIcon} conteggio={dati.blocchi.length}>
            <Nota className="px-1">
              Copie delle schede o dei test di libreria: RPE e minuti si scelgono
              all&apos;inserimento. Un test divide il carico in parti uguali fra gli attributi delle
              sue misure, e dopo Chiudi appello se ne scrivono i risultati dal blocco.
            </Nota>
            {dati.blocchi.length === 0 ? (
              <StatoVuoto titolo="Nessun blocco in questa seduta" />
            ) : (
              <ul className="grid gap-2 @3xl:grid-cols-2">
                {dati.blocchi.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
                  >
                    <span className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-background">
                      {b.testId ? (
                        <RulerIcon role="img" aria-label="Test di misura" className="size-5" />
                      ) : (
                        <RiquadroFamiglia famiglia={famigliaPrevalente(famiglieDelMix(b.mix))} />
                      )}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold break-words">{b.nome}</span>
                        {b.testId && (
                          <Badge variant="secondary">
                            {b.esecuzioneId ? 'Test · risultati salvati' : 'Test'}
                          </Badge>
                        )}
                      </span>
                      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <DatiScheda rpe={b.rpeTarget} minuti={b.minutiProgrammati} />
                        <MixPerFamiglia famiglie={famiglieDelMix(b.mix)} />
                      </span>
                      <span className="flex flex-wrap gap-1">
                        {nomiGruppo(b.gruppoIds).map((n) => (
                          <Badge key={n} variant="outline">
                            {n}
                          </Badge>
                        ))}
                      </span>
                    </span>
                    <AzioniBlocco
                      blocco={b}
                      risultati={risultatiDelBlocco(a.squadraStagioneId, a.stato, b)}
                      soloLettura={soloLettura}
                      inCorso={inCorso}
                      onTogli={() => void eliminaBlocco(b)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardVista>
        </TabsContent>
      </Tabs>

      {dialogoGruppo !== null && (
        <DialogoGruppo
          allenamentoId={a.id}
          esistente={dialogoGruppo === 'nuovo' ? undefined : dialogoGruppo}
          onChiudi={() => setDialogoGruppo(null)}
          onSalvato={async () => {
            setDialogoGruppo(null);
            await ricarica();
            toast.success('Gruppo salvato.');
          }}
        />
      )}
      {libreriaAperta && (
        <DialogoBlocco
          allenamentoId={a.id}
          gruppi={dati.gruppi}
          schede={libreria.dati}
          erroreSchede={libreria.errore}
          test={libreriaTest.dati}
          erroreTest={libreriaTest.errore}
          onChiudi={() => setLibreriaAperta(false)}
          onSalvato={async () => {
            setLibreriaAperta(false);
            await ricarica();
            toast.success('Blocco aggiunto.');
          }}
        />
      )}
      {conferma}
    </>
  );
}

/**
 * Le azioni sulla riga di un blocco. Da scheda: togli, con l'icona. Da test:
 * il menu «⋯» con «Risultati» (spenta prima di Chiudi appello) e «Togli il
 * blocco», spenta se il blocco ha già i risultati. In sola lettura resta il
 * rimando ai risultati salvati.
 */
function AzioniBlocco({
  blocco: b,
  risultati,
  soloLettura,
  inCorso,
  onTogli,
}: {
  blocco: BloccoSeduta;
  risultati: ReturnType<typeof risultatiDelBlocco>;
  soloLettura: boolean;
  inCorso: boolean;
  onTogli: () => void;
}) {
  if (soloLettura) {
    return risultati && 'a' in risultati && b.esecuzioneId ? (
      <ComandoIcona
        etichetta="Risultati del test"
        Icona={ClipboardPenLineIcon}
        size="icon-sm"
        a={risultati.a}
      />
    ) : null;
  }
  if (!risultati) {
    return (
      <ComandoIcona
        etichetta="Togli il blocco"
        Icona={Trash2Icon}
        size="icon-sm"
        disabled={inCorso}
        onClick={onTogli}
      />
    );
  }
  return (
    <MenuAzioni
      riga
      etichetta={`Azioni del blocco ${b.nome}`}
      voci={[
        {
          etichetta: 'Risultati',
          Icona: ClipboardPenLineIcon,
          ...('a' in risultati ? { a: risultati.a } : { disabled: true, motivo: risultati.motivo }),
        },
        {
          etichetta: 'Togli il blocco',
          Icona: Trash2Icon,
          onSelect: onTogli,
          distruttiva: true,
          disabled: inCorso || b.esecuzioneId !== null,
          motivo: b.esecuzioneId
            ? 'Ha i risultati del test: elimina prima l’esecuzione.'
            : undefined,
        },
      ]}
    />
  );
}

/** Blocchi, minuti e dose target (RPE × minuti, in UA) di un gruppo. */
function totaliGruppo(blocchi: BloccoSeduta[], gruppoId: string) {
  const suoi = blocchi.filter((b) => b.gruppoIds.includes(gruppoId));
  return {
    blocchi: suoi.length,
    minuti: suoi.reduce((t, b) => t + b.minutiProgrammati, 0),
    ua: suoi.reduce((t, b) => t + dose({ rpe: b.rpeTarget, minuti: b.minutiProgrammati }), 0),
  };
}

/** Il mix del blocco sommato per famiglia, come nelle righe della libreria. */
function famiglieDelMix(mix: BloccoSeduta['mix']): SchedaInElenco['famiglie'] {
  const famiglie: SchedaInElenco['famiglie'] = {};
  for (const v of mix) {
    const f = v.famiglia as FamigliaAttributo;
    famiglie[f] = (famiglie[f] ?? 0) + v.quota;
  }
  return famiglie;
}

/** False se, avvisati di cosa si perde, si rinuncia. */
async function togliBlocco(
  allenamentoId: string,
  bloccoId: string,
  chiedi: (domanda: Domanda) => Promise<boolean>,
): Promise<boolean> {
  const percorso = `/allenamenti/${allenamentoId}/blocchi/${bloccoId}`;
  try {
    await chiama(percorso, { metodo: 'DELETE' });
    return true;
  } catch (e) {
    if (!(e instanceof ErroreApi) || e.stato !== 409) throw e;
    const si = await chiedi({
      titolo: 'Togliere comunque il blocco?',
      testo: e.message,
      azione: 'Togli',
      distruttiva: true,
    });
    if (si) await chiama(`${percorso}?conferma=true`, { metodo: 'DELETE' });
    return si;
  }
}

function DialogoGruppo({
  allenamentoId,
  esistente,
  onChiudi,
  onSalvato,
}: {
  allenamentoId: string;
  esistente?: GruppoSeduta;
  onChiudi: () => void;
  onSalvato: () => Promise<void>;
}) {
  const [nome, setNome] = useState(esistente?.nome ?? '');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const salva = async () => {
    const esito = creaGruppoSchema.safeParse({ nome });
    if (!esito.success) {
      setErrori(erroriPerCampo(esito.error));
      return;
    }
    setErrori({});
    setErrore(null);
    setInCorso(true);
    try {
      await chiama(
        esistente
          ? `/allenamenti/${allenamentoId}/gruppi/${esistente.id}`
          : `/allenamenti/${allenamentoId}/gruppi`,
        { metodo: esistente ? 'PUT' : 'POST', corpo: esito.data },
      );
      await onSalvato();
    } catch (e) {
      setErrore(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{esistente ? 'Modifica gruppo' : 'Nuovo gruppo'}</DialogTitle>
          <DialogDescription>
            Un gruppo custom raccoglie giocatori che non seguono il lavoro del loro ruolo.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <Field data-invalid={Boolean(errori.nome)}>
            <FieldLabel htmlFor="nome-gruppo">Nome</FieldLabel>
            <Input
              id="nome-gruppo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              aria-invalid={Boolean(errori.nome)}
            />
            {errori.nome && <FieldError>{errori.nome}</FieldError>}
          </Field>
        </FieldGroup>
        {errore && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{errore}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </Button>
          <PulsanteInvio type="button" inCorso={inCorso} onClick={() => void salva()}>
            Salva
          </PulsanteInvio>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Da che cosa nasce il blocco: una scheda o un test, con RPE e minuti proposti. */
interface Sorgente {
  tipo: 'scheda' | 'test';
  id: string;
  nome: string;
  rpeRiferimento: number;
  minutiRiferimento: number;
}

function DialogoBlocco({
  allenamentoId,
  gruppi,
  schede,
  erroreSchede,
  test,
  erroreTest,
  onChiudi,
  onSalvato,
}: {
  allenamentoId: string;
  gruppi: GruppoSeduta[];
  schede: SchedaInElenco[] | null;
  erroreSchede: string | null;
  test: TestInElenco[] | null;
  erroreTest: string | null;
  onChiudi: () => void;
  onSalvato: () => Promise<void>;
}) {
  const [q, setQ] = useState('');
  const [fonte, setFonte] = useState<'scheda' | 'test'>('scheda');
  const [scelta, setScelta] = useState<Sorgente | null>(null);
  const [gruppoIds, setGruppoIds] = useState<string[]>([]);
  const [rpe, setRpe] = useState('');
  const [minuti, setMinuti] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const tutti = gruppi.map((g) => g.id);
  const tuttiSelezionati = tutti.length > 0 && tutti.every((id) => gruppoIds.includes(id));

  // I test entrano in seduta solo con RPE e minuti di riferimento.
  const sorgenti = useMemo((): Sorgente[] | null => {
    if (fonte === 'scheda') {
      return schede?.map((s) => ({ ...s, tipo: 'scheda' as const })) ?? null;
    }
    return (
      test?.flatMap((t) =>
        t.rpeRiferimento !== null && t.minutiRiferimento !== null
          ? [
              {
                tipo: 'test' as const,
                id: t.id,
                nome: t.nome,
                rpeRiferimento: t.rpeRiferimento,
                minutiRiferimento: t.minutiRiferimento,
              },
            ]
          : [],
      ) ?? null
    );
  }, [fonte, schede, test]);
  const varianti = new Set(schede?.filter((s) => s.schedaBaseId).map((s) => s.id));
  const misureDi = new Map(test?.map((t) => [t.id, t.misure.length]));

  const visibili = useMemo(() => {
    if (!sorgenti) return [];
    const testo = q.trim().toLocaleLowerCase('it');
    return sorgenti.filter((s) => !testo || s.nome.toLocaleLowerCase('it').includes(testo));
  }, [sorgenti, q]);

  const scegli = (s: Sorgente) => {
    setScelta(s);
    setRpe(String(s.rpeRiferimento));
    setMinuti(String(s.minutiRiferimento));
    setGruppoIds([]);
    setErrore(null);
  };

  const alterna = (id: string, acceso: boolean) => {
    setGruppoIds((attuali) =>
      acceso ? [...new Set([...attuali, id])] : attuali.filter((x) => x !== id),
    );
  };

  const salva = async () => {
    if (!scelta) return;
    const rpeTarget = Number(rpe);
    const minutiProgrammati = Number(minuti);
    if (!Number.isInteger(rpeTarget) || rpeTarget < 1 || rpeTarget > 10) {
      setErrore('RPE deve essere un intero tra 1 e 10.');
      return;
    }
    if (!Number.isInteger(minutiProgrammati) || minutiProgrammati < 1) {
      setErrore('I minuti devono essere un intero maggiore di 0.');
      return;
    }
    if (gruppoIds.length === 0) {
      setErrore('Scegli almeno un gruppo.');
      return;
    }
    setErrore(null);
    setInCorso(true);
    try {
      await chiama(`/allenamenti/${allenamentoId}/blocchi`, {
        metodo: 'POST',
        corpo: {
          [scelta.tipo === 'scheda' ? 'schedaId' : 'testId']: scelta.id,
          gruppoIds,
          rpeTarget,
          minutiProgrammati,
        },
      });
      await onSalvato();
    } catch (e) {
      setErrore(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !inCorso && !v && onChiudi()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{scelta ? scelta.nome : 'Aggiungi dalla libreria'}</DialogTitle>
          <DialogDescription>
            {scelta
              ? scelta.tipo === 'test'
                ? 'Scegli i gruppi. RPE e minuti valgono solo per questo blocco; il carico va in parti uguali agli attributi delle misure, e i risultati si scrivono dopo Chiudi appello.'
                : 'Scegli i gruppi. RPE e minuti valgono solo per questo blocco.'
              : fonte === 'scheda'
                ? 'Solo schede di esercizio attive. Una scheda partita non sta in un blocco di seduta.'
                : 'Solo test attivi con RPE e minuti di riferimento.'}
          </DialogDescription>
        </DialogHeader>
        {!scelta ? (
          <>
            <Tabs value={fonte} onValueChange={(v) => setFonte(v as 'scheda' | 'test')}>
              <TabsList className="w-full">
                <TabsTrigger value="scheda">Schede</TabsTrigger>
                <TabsTrigger value="test">Test</TabsTrigger>
              </TabsList>
            </Tabs>
            <Field>
              <FieldLabel htmlFor="libreria-q">Cerca</FieldLabel>
              <Input
                id="libreria-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nome"
              />
            </Field>
            {!sorgenti ? (
              <StatoCaricamento errore={fonte === 'scheda' ? erroreSchede : erroreTest} />
            ) : visibili.length === 0 ? (
              fonte === 'scheda' ? (
                <StatoVuoto titolo="Nessuna scheda corrisponde" />
              ) : (
                <StatoVuoto
                  titolo="Nessun test da mettere in seduta"
                  descrizione="Un test entra in seduta con RPE e minuti di riferimento: si aggiungono nel suo form."
                />
              )
            ) : (
              <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
                {visibili.map((s) => (
                  <li key={s.id}>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-start py-2"
                      onClick={() => scegli(s)}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{s.nome}</span>
                      <span className="text-muted-foreground">
                        RPE {s.rpeRiferimento} · {s.minutiRiferimento}′
                        {varianti.has(s.id) ? ' · variante' : ''}
                        {(misureDi.get(s.id) ?? 1) > 1 ? ` · ${misureDi.get(s.id)} misure` : ''}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={tuttiSelezionati}
                  onCheckedChange={(v) => setGruppoIds(v === true ? tutti : [])}
                />
                Seleziona tutti
              </label>
              <ul className="flex flex-col gap-2">
                {gruppi.map((g) => (
                  <li key={g.id}>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={gruppoIds.includes(g.id)}
                        onCheckedChange={(v) => alterna(g.id, v === true)}
                      />
                      {g.nome}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="rpe-blocco">RPE target</FieldLabel>
                <Input
                  id="rpe-blocco"
                  type="number"
                  min={1}
                  max={10}
                  value={rpe}
                  onChange={(e) => setRpe(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="minuti-blocco">Minuti programmati</FieldLabel>
                <Input
                  id="minuti-blocco"
                  type="number"
                  min={1}
                  value={minuti}
                  onChange={(e) => setMinuti(e.target.value)}
                />
              </Field>
            </div>
          </div>
        )}
        {errore && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Blocco non aggiunto</AlertTitle>
            <AlertDescription>{errore}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          {scelta ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setScelta(null)}
              disabled={inCorso}
            >
              Indietro
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={onChiudi}>
              Annulla
            </Button>
          )}
          {scelta && (
            <PulsanteInvio type="button" inCorso={inCorso} onClick={() => void salva()}>
              Aggiungi
            </PulsanteInvio>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
